import { DetailDataSource } from '../DetailDataSource';
import { EpgDetailDescriptionDataSource } from './EpgDetailDescriptionDataSource';
import { ISortedProgram } from '../../utility/EpgUtil';
import { AssetType, StreamType } from '../../../api/models/Shared.types';
import { EpgWatchAction } from '../actions/EpgWatchAction';
import { DetailTranslationKeys } from '../../../api/models/TranslationKeys';
import { ActionButton } from '../../../pages/detail/actionRowButtons/ActionButton';
import { timeStringToMs } from '../../utility/DateFormatter';
import { SharedFavouriteActionButton } from '../../../pages/detail/actionRowButtons/SharedFavouriteActionButton';
import { ShowDetailAction } from '../actions/ShowDetailAction';
import { RecordingSeasons, TabbedRecordingRibbonDataSource } from '../../cms/TabbedRecordingRibbonDataSource';
import { TabbedEpgEpisodesRibbonDataSource } from '../../cms/TabbedEpgEpisodesRibbonDataSource';
import {
  EpgCancelRecordingAction,
  EpgCreateRecordingAction,
  EpgDeleteRecordingAction,
  EpgEditRecordingAction,
} from '../actions/epgRecordingActions';
import { StaticPageUrls } from '../../../api/analytics/ClientEvents';
import { Backend } from '../../../api/Backend';
import { IProgramV5, IScheduleV5, ISeasonV5, ISeriesV5 } from '../../../api/models/AndromeTV.types';
import { IActionButtonDefinition, IDetailModel, NextEpisodeInfo } from '../detail.types';
import { ChannelModel } from '../../../api/EpgApi.types';
import { Recording } from '../../../api/user/RecordingApi.types';
import { getEpgWatchability } from './EpgWatchability';
import { ChannelManager } from '../../../api/ChannelManager';
import { RibbonDataSource } from '../../cms/RibbonDataSource';
import { ReminderActionButton } from '../../../pages/detail/actionRowButtons/ReminderActionButton';

export class EpgDetailDataSource extends DetailDataSource {
  protected program: IProgramV5 | undefined;
  protected series: ISeriesV5 | undefined;
  protected season: ISeasonV5 | undefined;
  protected schedule: IScheduleV5 | undefined;
  protected channel: ChannelModel | undefined;
  protected recording: Recording | undefined;
  protected pageType: AssetType | undefined;
  private seriesNextEpisode: NextEpisodeInfo | undefined;
  protected channelsList: ChannelModel[] | undefined;

  constructor(backend: Backend) {
    super(backend);
    this.program = undefined;
    this.series = undefined;
    this.season = undefined;
    this.schedule = undefined;
    this.channel = undefined;
    this.recording = undefined;
    this.pageType = undefined;
    this.seriesNextEpisode = undefined;
  }

  override logPageView() {
    this.backend.logService.trackPageView(StaticPageUrls.epgDetail);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override load(id: string): Promise<IDetailModel | undefined> {
    //equivalent of initializeVisualAssetForProgram in web
    //load main content
    const description = new EpgDetailDescriptionDataSource(
      this.backend,
      this.pageType,
      this.program,
      this.channel,
      this.schedule,
      this.season,
      this.recording
    );

    return Promise.all([this.getRibbons()]).then(([ribbons]) => {
      const buttons = this.getButtons(undefined);

      const res: IDetailModel = {
        description: description,
        images: {
          detailPoster: this.program?.image.detail ? this.program.image.detail : this.program?.image.poster,
        },
        buttons,
        ribbonDatasources: ribbons,
      };
      return res;
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getButtons(nextEpisodeInfo: ISortedProgram | undefined): IActionButtonDefinition[] {
    const result: IActionButtonDefinition[] = [];
    if (this.backend.isAuthorized()) {
      if (this.authShowWatchButton()) {
        const watchButton = this.getWatchButton();
        if (watchButton) result.push(watchButton);
      }
      if (this.authShowStartRecord()) {
        const recordButton = this.getStartRecordButton();
        if (recordButton) result.push(recordButton);
      } else if (this.authShowDeleteRecord()) {
        const deleteRecButton = this.getDeleteRecordButton();
        if (deleteRecButton) result.push(deleteRecButton);
      } else if (this.authShowCancelRecord()) {
        const cancelRecButton = this.getCancelRecordButton();
        if (cancelRecButton) result.push(cancelRecButton);
      }
    } else {
      // todo add unauthenticated watch button
    }

    if (this.backend.isAuthorized()) {
      if (this.pageType === AssetType.RECORDING) {
        const editRecordButton = this.getEditRecordButton();
        if (editRecordButton) result.push(editRecordButton);
      }
      result.push(this.getFavoriteButton());
      const favouriteChannelButton = this.getFavouriteChannelButton();
      if (favouriteChannelButton) result.push(favouriteChannelButton);

      if (this.showReminderButton()) {
        const showReminderButton = this.getReminderButton();
        if (showReminderButton) result.push(showReminderButton);
      }
    }

    if (this.series && this.pageType !== AssetType.RECORDING) {
      const openSeriesButton = this.getOpenSeriesButton();
      if (openSeriesButton) result.push(openSeriesButton);
    }

    return result;
  }

  getRibbons() {
    const id = this.series ? this.series.seriesId : this.program?.programId;
    const type = this.series ? AssetType.SERIES : AssetType.PROGRAM;

    let result: (RibbonDataSource | TabbedEpgEpisodesRibbonDataSource | TabbedRecordingRibbonDataSource)[] = [];
    const epRibbon = this.getEpisodesRibbon();
    if (epRibbon) result.push(epRibbon);
    if (id) result = result.concat(this.createRelatedDataSources(this.backend, type, id, true, this.channelsList));

    return this.getRecordingRibbon().then(rib => {
      if (rib) result.splice(this.pageType === AssetType.RECORDING ? 0 : 0, 0, rib); // can be used to place recording ribbon on second line
      return result;
    });
  }

  initChannels() {
    return ChannelManager.channels(this.backend).then(result => {
      this.channelsList = result;
    });
  }

  findRecordingForProgram(): Promise<Recording | undefined> {
    if (!this.program || !this.channel) {
      return Promise.resolve(undefined);
    }

    return this.backend.cachedRecordingsApi
      .recordingForProgram(this.backend, this.program.programId, this.channel.channelId)
      .then(result => {
        this.recording = result;
        return result;
      });
  }

  authShowWatchButton() {
    return getEpgWatchability(
      this.backend,
      this.schedule,
      this.channel,
      this.recording,
      this.pageType === AssetType.RECORDING
    ).showWatchButton;
  }

  getWatchButton(): IActionButtonDefinition | undefined {
    if (!this.channel || !this.schedule || !this.program) return undefined;
    return {
      type: ActionButton,
      actionHandler: new EpgWatchAction(
        this.backend,
        this.pageType,
        this.channel,
        this.schedule,
        this.program,
        this.series,
        this.season,
        this.recording,
        this.program?.adult,
        this.channelsList
      ),
      label: this.backend.translations.forKey(DetailTranslationKeys.detail_btn_watch_now),
    };
  }

  getEpgWatchAction() {
    return new EpgWatchAction(
      this.backend,
      this.pageType,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.channel,
      this.schedule,
      this.program,
      this.series,
      this.season,
      this.recording,
      this.program?.adult,
      this.channelsList
    );
  }

  authShowStartRecord() {
    const isInRecordingWindow = this.checkInRecordingWindow();
    const channelHasRights = this.channel?.hasOneOfCustomerRights([StreamType.NPVR]);
    return (
      this.pageType !== AssetType.SERIES &&
      isInRecordingWindow &&
      channelHasRights &&
      this.recording === undefined &&
      this.backend.sessionInfo?.npvr
    );
  }

  getStartRecordButton(): IActionButtonDefinition | undefined {
    if (!this.program) return undefined;
    return {
      type: ActionButton,
      actionHandler: new EpgCreateRecordingAction(
        this.backend,
        this.program.text.title,
        this.program.programId,
        this.series ? this.series.seriesId : undefined,
        this.season ? this.season.seasonId : undefined,
        this.channel?.channelId
      ),
      label: this.backend.translations.forKey(DetailTranslationKeys.detail_recording_start),
    };
  }

  getRecordingAction(): EpgCreateRecordingAction | undefined {
    if (!this.program) return undefined;
    return new EpgCreateRecordingAction(
      this.backend,
      this.program.text.title,
      this.program.programId,
      this.series ? this.series.seriesId : undefined,
      this.season ? this.season.seasonId : undefined,
      this.channel?.channelId
    );
  }

  checkInRecordingWindow() {
    let schedule = this.schedule;
    if (!schedule && this.recording) {
      schedule = {
        parentalControl: '',
        live: false,
        scheduleId: this.recording.schedule,
        published: { start: this.recording.start, end: this.recording.end },
        programId: this.recording.program,
        channelId: this.recording.channel,
      };
    }

    if (schedule) {
      const recordingStart = new Date();

      const now = new Date().getTime();

      if (this.channel?.npvrRecordHours) {
        recordingStart.setHours(recordingStart.getHours() - this.channel.npvrRecordHours);
        return (
          timeStringToMs(schedule.published.end) >= now ||
          (timeStringToMs(schedule.published.start) <= now &&
            timeStringToMs(schedule.published.start) > recordingStart.getTime())
        );
      } else {
        return timeStringToMs(schedule.published.end) >= now;
      }
    }

    return false;
  }

  authShowDeleteRecord() {
    return this.recording && (this.recording.isRecorded() || this.recording.isInProgress());
  }

  getDeleteRecordButton(): IActionButtonDefinition | undefined {
    if (!this.program?.text.title || !this.recording?.identifier) return undefined;
    return {
      type: ActionButton,
      actionHandler: new EpgDeleteRecordingAction(this.backend, this.program.text.title, this.recording.identifier),
      label: this.backend.translations.forKey(DetailTranslationKeys.detail_recording_delete),
    };
  }

  getDeleteRecordAction(): EpgDeleteRecordingAction | undefined {
    if (this.program && this.recording) {
      return new EpgDeleteRecordingAction(this.backend, this.program.text.title, this.recording.identifier);
    }
    return undefined;
  }

  authShowCancelRecord() {
    return this.recording?.isPlanned();
  }

  getCancelRecordButton(): IActionButtonDefinition | undefined {
    if (!this.program || !this.recording) return undefined;
    return {
      type: ActionButton,
      actionHandler: new EpgCancelRecordingAction(this.backend, this.recording, this.program),
      label: this.backend.translations.forKey(DetailTranslationKeys.detail_recording_cancel),
    };
  }

  getCancelRecordAction(): EpgCancelRecordingAction | undefined {
    if (this.program && this.recording) {
      return new EpgCancelRecordingAction(this.backend, this.recording, this.program);
    }
    return undefined;
  }

  getEditRecordButton(): IActionButtonDefinition | undefined {
    if (!this.program || !this.recording) return undefined;
    return {
      type: ActionButton,
      actionHandler: new EpgEditRecordingAction(
        this.backend,
        this.program.text.title,
        this.program.programId,
        this.recording
      ),
      label: this.backend.translations.forKey(DetailTranslationKeys.detail_btn_recording_edit),
    };
  }

  getFavoriteButton(): IActionButtonDefinition {
    const id = this.series ? this.series.seriesId : this.program?.programId;
    const type = this.series ? AssetType.SERIES : AssetType.PROGRAM;
    return {
      type: SharedFavouriteActionButton,
      assetId: id,
      assetType: type,
      assetIsAdult: this.program?.adult,
    };
  }

  getFavouriteChannelButton(): IActionButtonDefinition | undefined {
    if (!this.channel) return undefined;
    return {
      type: SharedFavouriteActionButton,
      assetId: this.channel.channelId,
      assetType: AssetType.CHANNEL,
      assetIsAdult: this.channel.adult,
    };
  }

  getOpenSeriesButton(): IActionButtonDefinition | undefined {
    if (!this.series) return undefined;
    const actionHandler = new ShowDetailAction(this.backend, this.series.seriesId, AssetType.SERIES);
    return {
      type: ActionButton,
      actionHandler,
      label: this.backend.translations.forKey(DetailTranslationKeys.detail_btn_series),
    };
  }

  getRecordingRibbon(): Promise<TabbedRecordingRibbonDataSource | undefined> {
    if (!this.series) return Promise.resolve(undefined);
    return this.backend.cachedRecordingsApi.recordingsForSeries(this.backend, this.series.seriesId).then(recordings => {
      this.sortRecordings(recordings);

      const sortedSeasons: RecordingSeasons[] = [];

      recordings.forEach(recording => {
        let sortedRecordingSeason = sortedSeasons.find(season => season.seasonId === recording.season);

        if (!sortedRecordingSeason) {
          if (recording.seasonOrdinal) {
            const title = `${this.backend.translations.forKey(DetailTranslationKeys.detail_season)} ${
              recording.seasonOrdinal
            }`;
            if (recording.season) {
              sortedRecordingSeason = {
                title,
                seasonId: recording.season,
                recordings: [],
              };
              sortedSeasons.push(sortedRecordingSeason);
            }
          } else if (this.series) {
            const title = this.series.text.title;
            if (title && recording.season) {
              sortedRecordingSeason = {
                title,
                seasonId: recording.season,
                recordings: [],
              };
              sortedSeasons.push(sortedRecordingSeason);
            }
          }
        }
        sortedRecordingSeason?.recordings.push(recording);
      });

      if (!this.channelsList) return undefined;
      return new TabbedRecordingRibbonDataSource(
        this.backend,
        sortedSeasons,
        this.backend.translations.forKey(DetailTranslationKeys.detail_tab_recordings),
        this.channelsList
      );
    });
  }

  sortRecordings(recordings: Recording[]) {
    recordings.sort((a, b) => {
      if (a.seasonOrdinal && b.seasonOrdinal && a.seasonOrdinal < b.seasonOrdinal) {
        return -1;
      } else if (a.seasonOrdinal && b.seasonOrdinal && a.seasonOrdinal > b.seasonOrdinal) {
        return 1;
      } else {
        if (a.programOrdinal && b.programOrdinal && a.programOrdinal < b.programOrdinal) {
          return -1;
        } else if (a.programOrdinal && b.programOrdinal && a.programOrdinal > b.programOrdinal) {
          return 1;
        } else {
          if (parseInt(a.plannedEnd, 10) > parseInt(b.plannedEnd, 10)) {
            return 1;
          } else if (parseInt(a.plannedEnd, 10) < parseInt(b.plannedEnd, 10)) {
            return -1;
          } else {
            return 0;
          }
        }
      }
    });
  }

  getEpisodesRibbon() {
    if (!this.series) {
      return undefined;
    }

    if (!this.channelsList) return undefined;
    return new TabbedEpgEpisodesRibbonDataSource(
      this.backend,
      this.series.seriesId,
      this.backend.translations.forKey(DetailTranslationKeys.detail_btn_all_episodes),
      this.channelsList,
      this.season?.seasonId
    );
  }

  showReminderButton() {
    if (!this.schedule) return false;
    const now = Date.now();
    const startTime = timeStringToMs(this.schedule?.published.start);
    if (startTime !== null && startTime > now) {
      return true;
    }
  }

  getReminderButton() {
    if (!this.schedule) return undefined;
    return {
      type: ReminderActionButton,
      assetId: this.schedule.scheduleId,
      assetType: AssetType.SCHEDULE,
    };
  }
}
